import { CaseAdmin } from "pwapi";

import { CaseAdminDetail } from "shared/reducers/admin/adminCaseReducer";
import {
  formatTimestampForDateTime,
  prepareDatetimeStringForAPI,
} from "shared/utils/dates";
import { shallowLeftDiff } from "shared/utils/formUtils";

// TODO: these could be util functions somewhere else
import { getRecentStatusLog } from "../casesTable/CasesTable";

export type CaseAdminDetailFormFields = CaseAdminDetail & {
  billing_components?: string;
};

export const getSelfPayProperties = (
  adminCase: Pick<
    CaseAdminDetail,
    "self_pay_with_attestation" | "indigent" | "self_pay"
  >
): string => {
  if (adminCase.self_pay_with_attestation) return "self_pay_with_attestation";
  if (adminCase.indigent) return "indigent";
  if (adminCase.self_pay) return "self_pay";
  return "not_applicable";
};

export const initialValuesForCase = (adminCase: CaseAdminDetail) => {
  const currentStatusLog = getRecentStatusLog(adminCase.status_log || []);

  return {
    ...adminCase,
    billing_components:
      adminCase.source === CaseAdmin.source.PW
        ? adminCase.bill_tc
          ? adminCase.bill_pc
            ? "none"
            : "tc_only"
          : "pc_only"
        : undefined,
    billing_properties: getSelfPayProperties(adminCase),
    collected_date:
      adminCase.collected_date &&
      formatTimestampForDateTime(
        adminCase.collected_date,
        adminCase.collected_date_timezone
      ),
    received_date:
      adminCase.received_date &&
      formatTimestampForDateTime(
        adminCase.received_date,
        adminCase.received_date_timezone
      ),
  };
};

export const submissionFieldsForCase = (
  adminCase: CaseAdminDetail,
  values?: Partial<ReturnType<typeof initialValuesForCase>>
) => {
  if (!values) values = initialValuesForCase(adminCase);

  return {
    id: values.id,
    accession: values.accession,
    priority: values.priority,
    active_assignment_id: values.active_assignment?.id || null,
    screen_assignment_id: values.screen_assignment?.id || null,
    finalize_assignment_id: values.finalize_assignment?.id || null,
    accessioning_lab_id: values.accessioning_lab?.id,
    status: values.status,
    grossed_by_cert_id: values.grossed_by_cert?.id,
    is_screening: values.is_screening || false,
    type: values.type,

    ...(adminCase.source === CaseAdmin.source.PW && {
      finalizing_signature_id: values.finalizing_signature?.id,
      billing_type: values.billing_type,
      bill_tc:
        values.billing_components === "none" ||
        values.billing_components === "tc_only",
      bill_pc:
        values.billing_components === "none" ||
        values.billing_components === "pc_only",
      self_pay: values.self_pay,
      self_pay_with_attestation: values.self_pay_with_attestation,
      indigent: values.indigent,
      external_accession: values.external_accession,
      collected_date:
        values.collected_date &&
        prepareDatetimeStringForAPI(
          values.collected_date,
          adminCase.collected_date_timezone
        ),
      received_date:
        values.received_date &&
        prepareDatetimeStringForAPI(
          values.received_date,
          adminCase.received_date_timezone
        ),
      grossed_by_external_lab_id: values.grossed_by_external_lab_id,
    }),
  };
};

export const shallowCaseComparison = (
  adminCase: CaseAdminDetail,
  values: Partial<ReturnType<typeof initialValuesForCase>>
) => {
  const oldValues = submissionFieldsForCase(adminCase);
  const newValues = submissionFieldsForCase(adminCase, values);

  return shallowLeftDiff<ReturnType<typeof submissionFieldsForCase>>(
    oldValues,
    newValues
  );
};
